import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BookOpen, PlayCircle, History, CalendarCheck2, Info, Archive, RefreshCcw } from "lucide-react";
import { toast } from "sonner";

function fmtDays(v) {
  const n = Number(v || 0);
  return Number.isInteger(n) ? String(n) : n.toFixed(1).replace(".", ",");
}
function fmtMonth(key) {
  if (!key || typeof key !== "string") return "—";
  const [y, m] = key.split("-").map(Number);
  const mois = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
  return `${mois[(m||1)-1]} ${y||""}`;
}

export default function LeaveSettings() {
  const [rules, setRules] = useState(null);
  const [history, setHistory] = useState([]);
  const [archives, setArchives] = useState([]);
  const [busy, setBusy] = useState(false);
  const [busyReset, setBusyReset] = useState(false);
  const [month, setMonth] = useState("");

  const load = async () => {
    try {
      const [r, h, a] = await Promise.all([
        api.get("/accruals/rules"),
        api.get("/accruals?limit=200"),
        api.get("/accruals/archives?limit=200"),
      ]);
      setRules(r.data); setHistory(h.data); setArchives(a.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setBusy(true);
    try {
      const body = month ? { month } : {};
      const { data } = await api.post("/accruals/run", body);
      if (data.ok === false) {
        toast.warning(`Refusé : ${data.reason}`);
      } else {
        toast.success(`Provisionnement effectué — ${data.base_applied} base, ${data.supp_applied} supplémentaires`);
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const runReset = async () => {
    if (!window.confirm("Confirmer : archiver et remettre à 0 les CP non consommés ? Cette action ne peut pas être annulée.")) return;
    setBusyReset(true);
    try {
      const { data } = await api.post("/accruals/reset-period", {});
      if (data.ok === false) toast.warning(`Refusé : ${data.reason}`);
      else toast.success(`Reset annuel effectué — ${data.archived} solde(s) archivé(s)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyReset(false); }
  };

  return (
    <div>
      <PageHeader
        title="Paramètres — Congés payés"
        subtitle="Convention collective de la Métallurgie. Le solde CP est provisionné automatiquement chaque 1er du mois."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Règle active */}
        <Card className="p-5 sm:p-6 border-slate-200 lg:col-span-2" data-testid="rules-card">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="h-4 w-4 text-[#0066FF]" />
            <h3 className="font-heading font-semibold text-slate-900">Règle active — Convention Métallurgie</h3>
          </div>
          {rules ? (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-md bg-slate-50 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total annuel</div>
                  <div className="mt-1 font-heading text-2xl font-bold text-slate-900">{rules.annual_total} <span className="text-sm text-slate-400 font-normal">j</span></div>
                </div>
                <div className="rounded-md bg-slate-50 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Mensuel</div>
                  <div className="mt-1 font-heading text-2xl font-bold text-slate-900">{fmtDays(rules.monthly_accrual)} <span className="text-sm text-slate-400 font-normal">j</span></div>
                </div>
                <div className="rounded-md bg-slate-50 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Période</div>
                  <div className="mt-1 text-slate-900 font-medium text-[13px] leading-tight pt-0.5">{rules.period_start} → {rules.period_end}</div>
                </div>
                <div className="rounded-md bg-[#0066FF]/5 p-3 border border-[#0066FF]/10">
                  <div className="text-[10px] uppercase tracking-wider text-[#0066FF] font-semibold">Effet</div>
                  <div className="mt-1 text-slate-900 font-medium text-[13px] leading-tight pt-0.5">{new Date(rules.effective_from).toLocaleDateString("fr-FR")}</div>
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Jours supplémentaires (annuels, ajoutés au 1er mai)</div>
                <div className="space-y-2">
                  {rules.supp_days_rules.map((r, i) => (
                    <div key={i} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2 text-sm">
                      <span className="text-slate-700">{r.label}</span>
                      <span className="rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-xs font-semibold">+{r.days} j</span>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-xs text-slate-500 flex items-start gap-1"><Info className="h-3 w-3 mt-0.5 shrink-0" />{rules.note}</p>
                <p className="mt-1 text-xs text-slate-500 flex items-start gap-1"><Info className="h-3 w-3 mt-0.5 shrink-0" />Le provisionnement concerne uniquement les comptes employés — l'administrateur RH n'est pas provisionné.</p>
              </div>
            </div>
          ) : <div className="text-sm text-slate-500">Chargement…</div>}
        </Card>

        {/* Action manuelle */}
        <Card className="p-5 sm:p-6 border-slate-200" data-testid="run-now-card">
          <div className="flex items-center gap-2 mb-4">
            <PlayCircle className="h-4 w-4 text-[#0066FF]" />
            <h3 className="font-heading font-semibold text-slate-900">Provisionner manuellement</h3>
          </div>
          <p className="text-sm text-slate-600 mb-4">
            Le provisionnement automatique s'exécute chaque 1er du mois. En cas de retard ou de correctif, vous pouvez le déclencher ci-dessous. Idempotent : aucun double compte.
          </p>
          <div className="space-y-3">
            <div>
              <Label htmlFor="month">Mois cible (facultatif)</Label>
              <Input
                id="month" data-testid="accrual-month-input"
                type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                placeholder="Mois en cours par défaut"
              />
              <p className="text-[11px] text-slate-500 mt-1">Format AAAA-MM. Laissez vide pour le mois en cours.</p>
            </div>
            <Button
              data-testid="run-accrual-button"
              onClick={runNow} disabled={busy}
              className="w-full bg-[#0066FF] hover:bg-[#0052CC]"
            >
              <CalendarCheck2 className="h-4 w-4 mr-2" />
              {busy ? "Provisionnement…" : "Provisionner ce mois"}
            </Button>
          </div>
        </Card>
      </div>

      {/* Reset annuel */}
      <Card className="mt-6 p-5 sm:p-6 border-amber-200 bg-amber-50/30" data-testid="reset-card">
        <div className="flex items-center gap-2 mb-3">
          <RefreshCcw className="h-4 w-4 text-amber-700" />
          <h3 className="font-heading font-semibold text-slate-900">Reset annuel du 30 avril</h3>
        </div>
        <p className="text-sm text-slate-600 mb-4 max-w-3xl">
          À la fin de la période de prise (30 avril), les congés payés non consommés sont <b>archivés puis remis à zéro</b> avant l'accrual du 1er mai. En cas de besoin, vous pouvez déclencher le reset manuellement ci-dessous. Cette action est idempotente pour l'année en cours.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={runReset} disabled={busyReset} data-testid="reset-button"
            className="bg-amber-600 hover:bg-amber-700"
          >
            <RefreshCcw className="h-4 w-4 mr-2" />
            {busyReset ? "Reset…" : "Lancer le reset annuel maintenant"}
          </Button>
          <span className="text-xs text-slate-500">Archive : <b>{archives.length}</b> ligne(s)</span>
        </div>
      </Card>

      {archives.length > 0 && (
        <Card className="mt-6 p-5 sm:p-6 border-slate-200" data-testid="archives-card">
          <div className="flex items-center gap-2 mb-4">
            <Archive className="h-4 w-4 text-[#0066FF]" />
            <h3 className="font-heading font-semibold text-slate-900">Journal d'archivage CP (par période)</h3>
          </div>
          <div className="divide-y divide-slate-100 -mx-1">
            {archives.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-3 py-2 px-1" data-testid={`archive-row-${a.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{a.user_name}</div>
                  <div className="text-xs text-slate-500 truncate">Période {a.period} · archivé le {new Date(a.archived_at).toLocaleDateString("fr-FR")}</div>
                </div>
                <span className="shrink-0 rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 text-xs font-semibold">
                  {fmtDays(a.cp_at_reset)} j non consommés
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Historique */}
      <Card className="mt-6 p-5 sm:p-6 border-slate-200" data-testid="history-card">
        <div className="flex items-center gap-2 mb-4">
          <History className="h-4 w-4 text-[#0066FF]" />
          <h3 className="font-heading font-semibold text-slate-900">Historique des provisionnements</h3>
        </div>
        {history.length === 0 ? (
          <div className="text-sm text-slate-500 py-8 text-center border border-dashed border-slate-200 rounded-lg">
            Aucun provisionnement encore effectué.
          </div>
        ) : (
          <div className="divide-y divide-slate-100 -mx-1">
            {history.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-3 py-3 px-1" data-testid={`accrual-row-${a.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{a.user_name}</div>
                  <div className="text-xs text-slate-500 truncate">{a.reason}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">{fmtMonth(a.month_key)} · par {a.applied_by}</div>
                </div>
                <div className="shrink-0 text-right">
                  <div className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${a.kind === "supp" ? "bg-violet-100 text-violet-700" : a.kind === "manual" ? "bg-slate-100 text-slate-700" : "bg-emerald-100 text-emerald-700"}`}>
                    +{fmtDays(a.days)} j
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
